import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, formatDate, todayISO } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import ConfirmDialog from "@/components/ConfirmDialog";
import { F } from "@/pages/Employees";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const JENIS = ["Kasbon", "Alpha", "Denda", "Angsuran Motor", "Pinjaman", "Potongan Lain"];

export default function Deductions() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "hr", "direktur"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", jenis: "Potongan Lain", tanggal: todayISO(), nominal: 0, keterangan: "", referensi: "" });
  const [del, setDel] = useState(null);

  const load = () => api.get("/deductions").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/employees").then((r) => setEmployees(r.data)); }, []);
  const empName = (id) => employees.find((e) => e.id === id)?.nama || "-";

  const save = async () => {
    if (!form.employee_id || !form.nominal) return toast.error("Pegawai & nominal wajib diisi");
    try { await api.post("/deductions", { ...form, nominal: Number(form.nominal) }); toast.success("Potongan ditambahkan"); setOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/deductions/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const columns = [
    { key: "employee_id", label: "Pegawai", sortable: true, sortValue: (r) => empName(r.employee_id), render: (r) => empName(r.employee_id) },
    { key: "jenis", label: "Jenis", sortable: true },
    { key: "tanggal", label: "Tanggal", render: (r) => formatDate(r.tanggal) },
    { key: "nominal", label: "Nominal", align: "right", render: (r) => rupiah(r.nominal) },
    { key: "keterangan", label: "Keterangan" },
    { key: "act", label: "Aksi", render: (r) => canWrite && <Button size="icon" variant="ghost" className="text-red-600" data-testid={`ded-delete-${r.id}`} onClick={() => setDel(r)}><Trash2 className="h-4 w-4" /></Button> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Potongan Gaji" subtitle="Potongan tambahan per pegawai (masuk perhitungan payroll)">
        {canWrite && <Button className="bg-blue-800 hover:bg-blue-900" data-testid="ded-add-btn" onClick={() => { setForm({ employee_id: "", jenis: "Potongan Lain", tanggal: todayISO(), nominal: 0, keterangan: "", referensi: "" }); setOpen(true); }}><Plus className="h-4 w-4 mr-1" /> Tambah Potongan</Button>}
      </PageHeader>
      <DataTable columns={columns} data={rows} searchKeys={["jenis", "keterangan"]} testid="ded-table" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Tambah Potongan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Pegawai *">
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="ded-f-emp"><SelectValue placeholder="Pilih pegawai" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.nama}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Jenis">
              <Select value={form.jenis} onValueChange={(v) => setForm({ ...form, jenis: v })}>
                <SelectTrigger data-testid="ded-f-jenis"><SelectValue /></SelectTrigger>
                <SelectContent>{JENIS.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Tanggal"><Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></F>
            <F label="Nominal *"><Input type="number" data-testid="ded-f-nominal" value={form.nominal} onChange={(e) => setForm({ ...form, nominal: e.target.value })} /></F>
            <F label="Referensi"><Input value={form.referensi} onChange={(e) => setForm({ ...form, referensi: e.target.value })} /></F>
            <F label="Keterangan"><Textarea value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={save} data-testid="ded-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} destructive testid="ded-delete-dialog"
        title="Hapus Potongan?" description="Yakin menghapus potongan ini?" confirmText="Hapus" onConfirm={doDelete} />
    </div>
  );
}
